import { ApolloProvider } from '@apollo/client/react';
import { ThemeProvider } from '@emotion/react';
import * as Sentry from '@sentry/nextjs';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState, useCallback, useEffect } from 'react';

import { Loading } from '@/components/blocks/Loading';
import { CustomHead } from '@/components/layouts/CustomHead';
import { SharedAppShell } from '@/components/layouts/SharedAppShell';
import { ErrorFallback } from '@/components/partials/ErrorFallback';
import { createApolloClient } from '@/graphql/apollo_client';
import { LiffProvider } from '@/providers/LiffProvider';
import { GlobalStyle } from '@/styles/global_style';
import { theme } from '@/styles/theme';

import type { ApolloClient } from '@apollo/client';

interface CustomAppProps extends AppProps {
  pageProps: {
    liffId?: string | undefined;
  };
}

function CustomApp({ Component, pageProps }: CustomAppProps) {
  const nextRouter = useRouter();
  const liffId = pageProps.liffId || (nextRouter.query.liffId as string) || '';
  const [client, setClient] = useState<ApolloClient | null>(() => null);
  const [userId, setUserId] = useState<string | null>(null);

  // liffIdとuserIdの両方が揃ったときにApolloClientを初期化
  useEffect(() => {
    if (liffId && userId && !client) {
      const newClient = createApolloClient(liffId, userId);
      setClient(newClient);
    }
  }, [liffId, userId, client]);

  const recreateClientPreservingCache = useCallback(
    (nextUserId: string | undefined) => {
      if (!client) return;

      const newClient = createApolloClient(liffId, nextUserId);
      newClient.cache.restore(client.cache.extract());
      setClient(newClient);
    },
    [liffId, client]
  );

  const handleUserIdChange = useCallback(
    (newUserId: string) => {
      if (liffId && newUserId) {
        setUserId(newUserId);
        recreateClientPreservingCache(newUserId);
      }
    },
    [liffId, recreateClientPreservingCache]
  );

  // 失敗した queryRef は ApolloClient 内の suspense キャッシュに残り続けるため、
  // クライアントを作り直さないと再試行しても再送が起きない
  const handleRetry = useCallback(
    (resetError: () => void) => {
      recreateClientPreservingCache(userId ?? undefined);
      resetError();
    },
    [recreateClientPreservingCache, userId]
  );

  // IndexPage or NotFoundPage の場合は、liffIdやApolloClientなしで直接レンダリング
  const notFoundPage = nextRouter.route === '/404' || nextRouter.route === '/';

  if (notFoundPage) {
    return (
      <Sentry.ErrorBoundary>
        <ThemeProvider theme={theme}>
          <Head>
            <meta charSet='UTF-8' />
            <meta
              name='viewport'
              content='width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1.0, viewport-fit=cover'
            />
            <meta name='format-detection' content='telephone=no' />
            <meta
              name='description'
              content='薬局ミニアプリ - 薬局でのサービスを便利に利用できます'
            />
            <title>薬局ミニアプリ</title>
          </Head>
          <GlobalStyle />
          <Component {...pageProps} />
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    );
  }

  if (!nextRouter.isReady) {
    return (
      <Sentry.ErrorBoundary>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <Loading />
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    );
  }

  // userIdが未設定またはApolloClientが未生成の場合はローディング状態を表示
  if (!client) {
    return (
      <Sentry.ErrorBoundary>
        <LiffProvider liffId={liffId} onUserIdChange={handleUserIdChange}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <Loading />
          </ThemeProvider>
        </LiffProvider>
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <Sentry.ErrorBoundary>
      <LiffProvider liffId={liffId} onUserIdChange={handleUserIdChange}>
        <ApolloProvider client={client}>
          <ThemeProvider theme={theme}>
            <CustomHead />
            <GlobalStyle />
            <SharedAppShell>
              {/* useSuspenseQuery は通信に失敗するとレンダー中に throw する。
                  ここで受けないと最上位まで飛んで画面が白くなる */}
              <Sentry.ErrorBoundary
                fallback={errorData => (
                  <ErrorFallback
                    onRetry={() => handleRetry(() => errorData.resetError())}
                  />
                )}
              >
                <Component {...pageProps} />
              </Sentry.ErrorBoundary>
            </SharedAppShell>
          </ThemeProvider>
        </ApolloProvider>
      </LiffProvider>
    </Sentry.ErrorBoundary>
  );
}

// Set the correct displayName in development
if (process.env.NODE_ENV !== 'production') {
  const displayName: string = CustomApp.name || 'Component';
  CustomApp.displayName = `withApollo(${displayName})`;
}

export default CustomApp;
